import { Component, inject } from '@angular/core';
import { EChartsOption } from 'echarts';

import { ITab } from '../../components/section-tabs/section-tabs.component';
import { FILTROS_BASE, FILTROS_DEFAULT } from '../../data/filters.data';
import { GLOSARIO } from '../../data/glossary.data';
import {
  DIAS_ENVIO,
  EMBUDO_ENTREGABILIDAD,
  ENVIOS_POR_CANAL,
  IChannelSeries,
  IFunnelRow,
} from '../../data/program-health.data';
import { FilterField } from '../../models/filter-option.model';
import { ChartService } from '../../services/chart.service';
import { ExportService, IExportBlock } from '../../services/export.service';
import { FilterService, IFilterContext } from '../../services/filter.service';

/** Término del glosario tal como lo entrega `glossary.data`. */
export interface IGlossaryTerm {
  termino: string;
  definicion: string;
}

/**
 * Programa (`/programa`) — «¿El programa está sano?».
 *
 * Dos pestañas: la entregabilidad de la base de contactos (embudo de 8 estatus
 * y envíos diarios por canal) y el glosario de 20 términos del programa.
 */
@Component({
  selector: 'app-program-page',
  standalone: false,
  templateUrl: './program-page.component.html',
  styleUrl: './program-page.component.scss',
})
export class ProgramPageComponent {

  private readonly chartService = inject(ChartService);
  private readonly filterService = inject(FilterService);
  private readonly exportService = inject(ExportService);

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR SECCION                                */
  /* -------------------------------------------------------------------------- */

  public readonly tabs: Array<ITab> = [
    { id: 'entregabilidad', label: 'Entregabilidad', icon: 'bi-send-check' },
    { id: 'glosario',       label: 'Glosario',       icon: 'bi-journal-text' },
  ];

  public tab = 'entregabilidad';

  public readonly filtros: Array<FilterField> = FILTROS_BASE;
  public seleccion: Record<string, Array<string>> = { ...FILTROS_DEFAULT };

  private ctx: IFilterContext = this.filterService.contexto(this.seleccion);

  /** Qué está acotando la vista; vacío cuando no hay filtros de universo. */
  public alcance: string = '';

  /* -------------------------------------------------------------------------- */
  /*                           ANCHOR ENTREGABILIDAD                            */
  /* -------------------------------------------------------------------------- */

  public embudo: Array<IFunnelRow> = EMBUDO_ENTREGABILIDAD;
  public enviosPorCanal!: EChartsOption;

  /** Series por canal, aparte de su `EChartsOption`, para poder exportarlas. */
  private envios: Array<IChannelSeries> = ENVIOS_POR_CANAL;

  /** Referencia de la barra de magnitud: el escalón más alto del embudo. */
  private totalMaximo: number = Math.max(...EMBUDO_ENTREGABILIDAD.map((fila) => fila.total));

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR GLOSARIO                               */
  /* -------------------------------------------------------------------------- */

  public readonly documento = 'Glosario Zorro Abarrotero';
  public readonly nota = 'Documento de referencia del programa NPS';
  public readonly terminos: Array<IGlossaryTerm> = GLOSARIO;

  public busqueda = '';
  public visibles: Array<IGlossaryTerm> = GLOSARIO;

  constructor() {
    this.aplicar();
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR FILTROS                                */
  /* -------------------------------------------------------------------------- */

  public onFiltros(valores: Record<string, Array<string>>): void {
    this.seleccion = valores;
    this.ctx = this.filterService.contexto(valores);
    this.aplicar();
  }

  /**
   * La entregabilidad son totales de envío: acotar el universo no cambia los
   * porcentajes del embudo —el 91.53% de higienizados es el mismo en una región
   * que en el país—, cambia cuántos registros hay detrás. Por eso aquí se
   * escalan los totales y se respetan los porcentajes publicados.
   */
  private aplicar(): void {
    const factor = this.filterService.factorMuestra(this.ctx);

    this.alcance = this.filterService.resumenAlcance(this.ctx);

    this.embudo = EMBUDO_ENTREGABILIDAD.map((fila) => ({
      ...fila,
      total: Math.max(0, Math.round(fila.total * factor)),
    }));
    this.totalMaximo = Math.max(...this.embudo.map((fila) => fila.total));

    this.envios = ENVIOS_POR_CANAL.map((serie) => ({
      ...serie,
      data: serie.data.map((valor) => Math.max(0, Math.round(valor * factor))),
    }));
    this.enviosPorCanal = this.chartService.groupedColumn(DIAS_ENVIO, this.envios, true);
  }

  /* -------------------------------------------------------------------------- */
  /*                            ANCHOR EXPORTACION                              */
  /* -------------------------------------------------------------------------- */

  /**
   * Baja la pestaña abierta. El glosario también se exporta: es la pestaña que
   * más se pide por escrito, precisamente porque es documentación.
   */
  public descargar(): void {
    const glosario = this.tab === 'glosario';

    this.exportService.csv(
      this.bloques(),
      this.exportService.nombreArchivo('nps', 'programa', glosario ? 'glosario' : 'entregabilidad'),
      {
        titulo: `Programa · ${glosario ? 'Glosario' : 'Entregabilidad'}`,
        // El glosario no depende del periodo ni de la sucursal.
        filtros: glosario
          ? (this.busqueda.trim() ? `búsqueda «${this.busqueda.trim()}»` : '')
          : this.filterService.descripcion(this.seleccion, this.filtros),
      },
    );
  }

  private bloques(): Array<IExportBlock> {
    if (this.tab === 'glosario') {
      return [{
        titulo: this.documento,
        columnas: [
          { key: 'termino', title: 'Término' },
          { key: 'definicion', title: 'Definición' },
        ],
        filas: this.visibles,
      }];
    }

    return [
      {
        titulo: 'Embudo de entregabilidad',
        columnas: [
          { key: 'estatus', title: 'Estatus' },
          { key: 'total', title: 'Total' },
          { key: 'porcentaje', title: 'Porcentaje' },
        ],
        filas: this.embudo,
      },
      {
        titulo: 'Envíos por día y canal',
        columnas: [
          { key: 'dia', title: 'Día' },
          ...this.envios.map((serie) => ({ key: serie.name, title: serie.name })),
        ],
        // La gráfica es una serie por canal; la tabla es una fila por día, con
        // el salto de línea del eje convertido en un solo texto.
        filas: DIAS_ENVIO.map((dia, i) => this.envios.reduce(
          (fila, serie) => ({ ...fila, [serie.name]: serie.data[i] ?? 0 }),
          { dia: dia.replace(/\n/g, ' ') } as Record<string, any>,
        )),
      },
    ];
  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR anchoBarra                              */
  /* -------------------------------------------------------------------------- */

  /**
   * Ancho de la barra de fondo de cada fila del embudo, en porcentaje.
   * La escala es de raíz cuadrada a propósito: el embudo va de 1.46M a 8.2k y
   * en escala lineal las seis últimas filas se quedarían sin barra visible.
   * Sólo es un apoyo visual; el dato exacto lo dan las columnas de la tabla.
   */
  public anchoBarra(fila: IFunnelRow): string {
    const referencia: number = Math.sqrt(this.totalMaximo);
    const proporcion: number = referencia ? Math.sqrt(fila.total) / referencia : 0;

    return `${Math.max(3, Math.round(proporcion * 100))}%`;
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR filtrar                                */
  /* -------------------------------------------------------------------------- */

  /** Filtra el glosario por término. Se recalcula al teclear, no en cada ciclo
   *  de detección de cambios. */
  public filtrar(): void {
    const aguja: string = this.normalizar(this.busqueda);

    this.visibles = aguja
      ? this.terminos.filter((item) => this.normalizar(item.termino).includes(aguja))
      : this.terminos;
  }

  public limpiarBusqueda(): void {
    this.busqueda = '';
    this.filtrar();
  }

  /** Sin acentos y en minúsculas: buscar «recomendacion» debe encontrar
   *  «Driver de recomendación». */
  private normalizar(texto: string): string {
    return texto
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }
}
